<template>
  <div class= "min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
      <header class="mb-8 text-center sm:text-left">
        <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
          Minhas <span class="bg-clip-text text-transparent bg-gradient-to-r from-brand-500 to-brand-400">Metas
            Financeiras</span>
        </h1>
        <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Defina e acompanhe seus objetivos para alcançar seus sonhos.
        </p>
      </header>

      <div class="mb-8">
        <button @click="handleNovaMeta" class="btn-primary">
          <span class="material-symbols-outlined mr-2">add_circle</span>
          Adicionar Meta
        </button>
      </div>

      <div v-if="sortedMetas.length === 0" class="card px-8 py-12 text-center space-y-3">
        <div class="mx-auto size-14 rounded-xl bg-slate-100 dark:bg-brand-700/50 grid place-items-center">
          <span class="material-symbols-outlined text-4xl text-slate-500 dark:text-brand-400">flag</span>
        </div>
        <p class="font-medium text-slate-800 dark:text-slate-200">Você ainda não adicionou nenhuma meta.</p>
        <p class="text-sm text-slate-600 dark:text-slate-400">Comece agora a planejar seus objetivos.</p>
        <button @click="handleNovaMeta" class="btn-secondary mt-2">Criar primeira meta</button>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
        <div v-for="metaItem in sortedMetas" :key="metaItem.id" class="card overflow-hidden">
          <div
            class="px-6 py-4 border-b border-slate-200 dark:border-brand-700 bg-slate-50/70 dark:bg-brand-900/40 flex items-center gap-3">
            <div
              class="size-10 rounded-lg bg-brand-500/10 text-brand-600 dark:bg-brand-500/20 dark:text-brand-400 grid place-items-center">
              <span class="material-symbols-outlined text-xl">{{ metaItem.icon || 'savings' }}</span>
            </div>
            <h3 class="font-semibold text-slate-900 dark:text-slate-100 truncate">
              {{ metaItem.title }}
            </h3>
          </div>

          <div class="p-6 space-y-6">
            <div class="space-y-2">
              <div class="flex items-baseline justify-between">
                <span class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Progresso</span>
                <span class="text-sm font-semibold text-brand-600 dark:text-brand-400">
                  {{ getProgress(metaItem).toFixed(0) }}%
                </span>
              </div>
              <div class="progress">
                <div class="progress-bar" :style="{ width: getProgress(metaItem) + '%' }"></div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4 pt-2">
              <div class="stat">
                <p class="stat-k">Alcançado</p>
                <p class="stat-v text-emerald-600 dark:text-emerald-400">
                  {{ formatCurrency(metaItem.currentAmount) }}
                </p>
              </div>
              <div class="stat">
                <p class="stat-k">Meta</p>
                <p class="stat-v">
                  {{ formatCurrency(metaItem.targetAmount) }}
                </p>
              </div>
            </div>
          </div>

          <div
            class="px-4 py-3 border-t border-slate-200 dark:border-brand-700 bg-slate-50/50 dark:bg-brand-900/30 flex items-center justify-end gap-2">
            <button @click="handleEditMeta(metaItem)" title="Editar"
              class="icon-btn text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-brand-700/60">
              <span class="material-symbols-outlined text-[18px]">edit</span>
            </button>
            <button @click="confirmDelete(metaItem.id)" title="Excluir"
              class="icon-btn text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20">
              <span class="material-symbols-outlined text-[18px]">delete</span>
            </button>
          </div>
        </div>
      </div>

      <transition name="modal">
        <div v-if="showModal" @click.self="closeModal"
          class="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4">
          <div class="modal-card relative w-full max-w-lg rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800">
            <header class="px-6 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ isEditing ? 'Editar Meta' : 'Nova Meta' }}
              </h3>
              <button @click="closeModal" :disabled="isSubmitting"
                class="icon-btn hover:bg-slate-100 dark:hover:bg-slate-800">
                <span class="material-symbols-outlined">close</span>
              </button>
            </header>

            <form @submit.prevent="handleSubmit" class="p-6 space-y-5">
              <div class="space-y-2">
                <label for="title" class="label">Título da Meta</label>
                <input id="title" v-model="meta.title" type="text" required placeholder="Ex: Viagem para a Europa"
                  class="input" />
              </div>

              <div class="space-y-2" ref="iconDropdownContainer">
                <label class="label">Ícone</label>
                <button type="button" @click="isIconDropdownOpen = !isIconDropdownOpen"
                  class="input flex items-center justify-between text-left">
                  <span class="flex items-center gap-2">
                    <span class="material-symbols-outlined text-base text-slate-600 dark:text-slate-300">
                      {{ meta.icon || 'savings' }}
                    </span>
                    <span class="text-slate-700 dark:text-slate-200">{{ selectedIconLabel }}</span>
                  </span>
                  <span class="material-symbols-outlined text-slate-400">unfold_more</span>
                </button>

                <transition name="dropdown">
                  <div v-if="isIconDropdownOpen"
                    class="absolute z-10 mt-1 p-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg max-h-60 overflow-y-auto">
                    <div class="grid grid-cols-6 gap-1">
                      <button v-for="icone in iconesDisponiveis" :key="icone.value" type="button" :title="icone.label"
                        @click="selectIcon(icone.value)"
                        class="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        :class="{ 'bg-brand-500/10 dark:bg-slate-900 ring-1 ring-brand-200 dark:ring-slate-600': meta.icon === icone.value }">
                        <span class="material-symbols-outlined text-slate-700 dark:text-slate-300">{{ icone.value
                          }}</span>
                      </button>
                    </div>
                  </div>
                </transition>
              </div>

              <div v-if="meta.icon === 'payments'" class="pt-4 border-t border-slate-200 dark:border-slate-800">
                <div v-if="isFetchingDividas" class="flex items-center gap-2 text-sm text-slate-500">
                  <span class="material-symbols-outlined animate-spin">progress_activity</span>
                  Calculando dívidas de {{ nomeMesAtual }}...
                </div>
                <div v-else class="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-4">
                  <p class="text-sm text-slate-600 dark:text-slate-400">
                    Valor alvo definido com a soma das dívidas pendentes (fixas + cartão) para <b>{{ nomeMesAtual
                      }}</b>.
                  </p>
                  <p class="mt-2 text-2xl font-bold text-brand-600 dark:text-brand-400">
                    {{ formatCurrency(meta.targetAmount) }}
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div class="space-y-2">
                  <label for="currentAmount" class="label">Valor Atual (R$)</label>
                  <input id="currentAmount" v-model.number="meta.currentAmount" type="number" step="0.01" required
                    placeholder="0,00" class="input" />
                </div>
                <div class="space-y-2">
                  <label for="targetAmount" class="label">Valor Alvo (R$)</label>
                  <input id="targetAmount" v-model.number="meta.targetAmount" type="number" step="0.01" required
                    placeholder="1.000,00" class="input" :readonly="meta.icon === 'payments'"
                    :class="{ 'bg-slate-100 dark:bg-slate-700/50 cursor-not-allowed': meta.icon === 'payments' }" />
                </div>
              </div>

              <div class="pt-2 flex justify-end gap-3">
                <button type="button" @click="closeModal" :disabled="isSubmitting"
                  class="btn-secondary">Cancelar</button>
                <button type="submit" :disabled="isSubmitting" class="btn-primary">
                  <span v-if="isSubmitting"
                    class="material-symbols-outlined animate-spin mr-2 text-lg">progress_activity</span>
                  {{ isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Meta' : 'Adicionar Meta') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>

      <transition name="modal">
        <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false"
          class="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4">
          <div class="modal-card w-full max-w-md overflow-hidden">
            <div class="p-8 text-center space-y-4">
              <div
                class="mx-auto size-12 rounded-xl grid place-items-center bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400">
                <span class="material-symbols-outlined text-3xl">warning</span>
              </div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Excluir Meta?</h3>
              <p class="text-sm text-slate-600 dark:text-slate-400">Essa ação é permanente.</p>
              <div class="flex justify-center gap-3 pt-1">
                <button @click="showDeleteConfirm = false" type="button" class="btn-secondary">Cancelar</button>
                <button @click="executeDelete" class="btn-danger">Sim, Excluir</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="modal">
        <div v-if="showUnsavedChangesConfirm" @click.self="showUnsavedChangesConfirm = false"
          class="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4">
          <div class="modal-card w-full max-w-md overflow-hidden">
            <div class="p-8 text-center space-y-4">
              <div
                class="mx-auto size-12 rounded-xl grid place-items-center bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                <span class="material-symbols-outlined text-3xl">warning</span>
              </div>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Descartar alterações?</h3>
              <p class="text-sm text-slate-600 dark:text-slate-400">Você possui alterações não salvas.</p>
              <div class="flex justify-center gap-3 pt-1">
                <button @click="showUnsavedChangesConfirm = false" type="button" class="btn-secondary">Voltar</button>
                <button @click="handleConfirmClose" class="btn-danger">Descartar</button>
              </div>
            </div>
          </div>
        </div>
      </transition>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const router = useRouter();
const metas = ref([]);
const showModal = ref(false);
const isEditing = ref(false);
const isSubmitting = ref(false);
const showDeleteConfirm = ref(false);
const itemParaDeletar = ref(null);

const isIconDropdownOpen = ref(false);
const iconDropdownContainer = ref(null);

const isMobile = ref(false);
const checkDevice = () => {
  isMobile.value = window.innerWidth < 640;
};

const meta = ref({ id: null, title: '', icon: 'savings', currentAmount: 0, targetAmount: 1000 });
const apiUrl = 'https://backend.controlease.com.br/metas';

const pristineMeta = ref(null);
const showUnsavedChangesConfirm = ref(false);

const dividasDeGastos = ref([]);
const dividasDeCartao = ref([]);
const isFetchingDividas = ref(false);

const iconesDisponiveis = [
  { value: 'savings', label: 'Economias / Poupança' }, { value: 'account_balance', label: 'Reserva de Emergência' },
  { value: 'home', label: 'Casa Própria' }, { value: 'directions_car', label: 'Carro Novo' },
  { value: 'flight_takeoff', label: 'Viagem' }, { value: 'school', label: 'Educação / Estudos' },
  { value: 'work', label: 'Carreira / Negócio' }, { value: 'celebration', label: 'Evento / Festa' },
  { value: 'devices', label: 'Eletrônicos / Gadgets' }, { value: 'payments', label: 'Quitar Dívidas' },
  { value: 'add_shopping_cart', label: 'Grande Compra' }, { value: 'flag', label: 'Outro Objetivo' },
];

const authConfig = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const fetchMetas = async () => {
  try { const r = await axios.get(apiUrl, authConfig()); metas.value = r.data || []; }
  catch (e) { console.error('Erro ao buscar metas:', e); }
};

const fetchDividas = async () => {
  if (dividasDeGastos.value.length && dividasDeCartao.value.length) return;
  isFetchingDividas.value = true;
  try {
    const [gastos, cartao] = await Promise.all([
      axios.get('https://backend.controlease.com.br/gastos', authConfig()),
      axios.get('https://backend.controlease.com.br/gastos-cartao', authConfig())
    ]);
    dividasDeGastos.value = (gastos.data || []).map(g => ({ ...g, type: 'gasto' }));
    dividasDeCartao.value = (cartao.data || []).map(g => ({ ...g, type: 'cartao' }));
  } catch (e) { console.error('Não foi possível buscar as dívidas.', e); }
  finally { isFetchingDividas.value = false; }
};

const calcularEdefinirMetaDividasAtuais = async () => {
  if (meta.value.icon !== 'payments') return;
  await fetchDividas();
  const hoje = new Date();
  const chave = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const todas = [...dividasDeGastos.value, ...dividasDeCartao.value];
  const total = todas
    .filter(d => d.status === 'PENDENTE' && d.dataPagamento?.startsWith(chave))
    .reduce((s, d) => s + (d.type === 'cartao' ? (d.valorParcela || 0) : (d.valor || 0)), 0);
  meta.value.targetAmount = total > 0 ? total : 1;
};

const nomeMesAtual = computed(() => {
  const nome = new Date().toLocaleString('pt-BR', { month: 'long' });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
});

const handleNovaMeta = () => {
  if (isMobile.value) {
    router.push({ name: 'MetasAdd' })
  } else {
    showAddMetaModal()
  }
}

const handleEditMeta = (metaItem) => {
  if (isMobile.value) {
    router.push({ name: 'MetasEdit', params: { id: metaItem.id } })
  } else {
    editMeta(metaItem)
  }
}

const showAddMetaModal = () => {
  resetForm();
  pristineMeta.value = JSON.parse(JSON.stringify(meta.value));
  showModal.value = true;
  if (meta.value.icon === 'payments') calcularEdefinirMetaDividasAtuais();
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    if (meta.value.targetAmount <= 0) { alert('O valor alvo deve ser maior que zero.'); isSubmitting.value = false; return; }
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${meta.value.id}`, meta.value, authConfig());
    } else {
      await axios.post(apiUrl, meta.value, authConfig());
    }
    await fetchMetas();
    forceCloseModal();
  } catch (e) {
    console.error('Erro ao salvar meta:', e);
    alert('Erro ao salvar meta. Verifique os dados e tente novamente.');
  } finally {
    isSubmitting.value = false;
  }
};

const editMeta = (m) => {
  meta.value = { ...m };
  pristineMeta.value = { ...m };
  isEditing.value = true;
  showModal.value = true;
  if (meta.value.icon === 'payments') calcularEdefinirMetaDividasAtuais();
};

const confirmDelete = (id) => { itemParaDeletar.value = id; showDeleteConfirm.value = true; };

const executeDelete = async () => {
  if (itemParaDeletar.value == null) return;
  try {
    await axios.delete(`${apiUrl}/${itemParaDeletar.value}`, authConfig());
    await fetchMetas();
  } catch (e) { console.error('Erro ao excluir meta:', e); alert('Erro ao excluir meta.'); }
  finally { showDeleteConfirm.value = false; itemParaDeletar.value = null; }
};

const isFormDirty = computed(() => pristineMeta.value && JSON.stringify(meta.value) !== JSON.stringify(pristineMeta.value));

const closeModal = () => {
  if (isSubmitting.value) return;
  if (isFormDirty.value) { showUnsavedChangesConfirm.value = true; }
  else { forceCloseModal(); }
};

const forceCloseModal = () => { showModal.value = false; resetForm(); };
const handleConfirmClose = () => { showUnsavedChangesConfirm.value = false; forceCloseModal(); };

const resetForm = () => { meta.value = { id: null, title: '', icon: 'savings', currentAmount: 0, targetAmount: 1000 }; isEditing.value = false; pristineMeta.value = null; };

const formatCurrency = (v) => typeof v === 'number'
  ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  : 'R$ 0,00';

const getProgress = (m) => {
  if (!m || m.targetAmount <= 0) return 0;
  const p = (m.currentAmount / m.targetAmount) * 100;
  return Math.min(Math.max(p, 0), 100);
};

const sortedMetas = computed(() => [...(metas.value || [])].sort((a, b) => (a.title || '').localeCompare(b.title || '')));
const selectedIconLabel = computed(() => iconesDisponiveis.find(i => i.value === meta.value.icon)?.label || 'Selecione um ícone');
const selectIcon = (v) => { meta.value.icon = v; isIconDropdownOpen.value = false; };

const handleClickOutside = (e) => {
  if (iconDropdownContainer.value && !iconDropdownContainer.value.contains(e.target)) {
    isIconDropdownOpen.value = false;
  }
};

watch(() => meta.value.icon, (n) => { if (n === 'payments' && showModal.value) calcularEdefinirMetaDividasAtuais(); }); // Só calcula se o modal estiver aberto
watch(isIconDropdownOpen, (o) => { o ? document.addEventListener('click', handleClickOutside) : document.removeEventListener('click', handleClickOutside); });

onMounted(() => {
  fetchMetas();
  checkDevice();
  window.addEventListener('resize', checkDevice);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', checkDevice);
});
</script>

<style scoped>
.card {
  @apply bg-white dark:bg-brand-800 rounded-2xl shadow-sm ring-1 ring-slate-200 dark:ring-brand-700;
}

.modal-card {
  @apply bg-white dark:bg-slate-900 rounded-2xl shadow-xl;
}

.input {
  @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500;
}

.label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300;
}

.btn-primary {
  @apply inline-flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-medium py-2.5 px-5 rounded-xl text-sm transition-colors shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30;
}

.btn-secondary {
  @apply inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-medium py-2.5 px-5 rounded-xl text-sm transition-colors;
}

.btn-danger {
  @apply inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-medium py-2.5 px-5 rounded-xl text-sm transition-colors;
}

.icon-btn {
  @apply p-2.5 rounded-lg transition-colors;
}

.stat {
  @apply space-y-1;
}

.stat-k {
  @apply text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400;
}

.stat-v {
  @apply text-lg font-semibold text-slate-900 dark:text-slate-100 tabular-nums;
}

.progress {
  @apply h-2.5 w-full rounded-full bg-slate-200 dark:bg-brand-700/70 overflow-hidden;
}

.progress-bar {
  @apply h-full bg-brand-500 rounded-full transition-all;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity .18s ease, transform .18s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(.98);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity .12s ease, transform .12s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.size-10 {
  width: 2.5rem;
  height: 2.5rem;
}

.size-12 {
  width: 3rem;
  height: 3rem;
}

.size-14 {
  width: 3.5rem;
  height: 3.5rem;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>